import { type FormEvent, useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  canUsePasskeys,
  issueSetupTotp,
  registerSetupPasskey,
  setSetupPassword,
  verifySetupTotp,
} from "./api";
import { startFragmentExchange } from "./fragment-secret";
import { renderTotpQrDataUrl } from "./qr";
import type { SetupState } from "./schemas";
import styles from "./AuthScreen.module.css";

export type SetupScreenProps = {
  readonly onComplete: () => void;
};

type LocalTotpMaterial = {
  readonly qrDataUrl: string;
  readonly backupCodes: readonly string[];
};

export function SetupScreen({ onComplete }: SetupScreenProps) {
  const { t } = useTranslation();
  const [registrationState, setRegistrationState] = useState<SetupState["state"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const materialRef = useRef<LocalTotpMaterial | null>(null);
  const materialEpochRef = useRef(0);
  const completedRef = useRef(false);
  const [, refreshMaterial] = useReducer((value: number) => value + 1, 0);

  const finish = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    materialRef.current = null;
    materialEpochRef.current += 1;
    refreshMaterial();
    onComplete();
  };

  useEffect(() => {
    let active = true;
    void startFragmentExchange().then((result) => {
      if (!active) return;
      if (result.kind !== "setup" || result.status !== "ready") {
        setErrorKey("auth.errors.invalidLink");
        setLoading(false);
        return;
      }
      if (["passkey_verified", "totp_verified", "complete"].includes(result.state)) {
        finish();
        return;
      }
      setRegistrationState(result.state);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => () => {
    materialEpochRef.current += 1;
    materialRef.current = null;
  }, []);

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setErrorKey(null);
    try {
      await operation();
    } catch {
      setErrorKey("auth.errors.generic");
    } finally {
      setBusy(false);
    }
  };

  const createPasskey = () => {
    if (!canUsePasskeys()) {
      setErrorKey("auth.errors.passkeyUnavailable");
      return;
    }
    void run(async () => {
      await registerSetupPasskey();
      finish();
    });
  };

  const createTotpMaterial = async () => {
    const epoch = ++materialEpochRef.current;
    const material = await issueSetupTotp();
    const qrDataUrl = await renderTotpQrDataUrl(material.totpUri);
    if (materialEpochRef.current !== epoch) return;
    materialRef.current = { qrDataUrl, backupCodes: material.backupCodes };
    setRegistrationState("password_set");
    refreshMaterial();
  };

  const submitPassword = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmedPassword) {
      setErrorKey("auth.errors.passwordMismatch");
      return;
    }
    const submittedPassword = password;
    setPassword("");
    setConfirmedPassword("");
    void run(async () => {
      await setSetupPassword(submittedPassword);
      await createTotpMaterial();
    });
  };

  const retryTotp = () => {
    void run(createTotpMaterial);
  };

  const submitTotp = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submittedCode = totpCode;
    setTotpCode("");
    void run(async () => {
      await verifySetupTotp(submittedCode);
      finish();
    });
  };

  const material = materialRef.current;
  const isExpired = registrationState === "expired";

  return (
    <section className={styles.screen} aria-labelledby="setup-title" aria-busy={loading || busy}>
      <div className={styles.cardWide}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>{t("app.name")}</p>
          <h1 id="setup-title">{t("auth.setup.title")}</h1>
          <p>{t("auth.setup.description")}</p>
        </header>

        {loading ? (
          <p className={styles.loadingText}>{t("auth.setup.loading")}</p>
        ) : isExpired ? (
          <p className={styles.help}>{t("auth.setup.expired")}</p>
        ) : registrationState === "created" || registrationState === "passkey_options_issued" ? (
          <div className={styles.setupChoices}>
            <section className={styles.choice} aria-labelledby="setup-passkey-title">
              <h2 id="setup-passkey-title">{t("auth.setup.passkeyTitle")}</h2>
              <p className={styles.help}>{t("auth.setup.passkeyHelp")}</p>
              <button className={styles.primaryButton} type="button" onClick={createPasskey} disabled={busy}>
                {busy ? t("auth.common.working") : t("auth.setup.createPasskey")}
              </button>
            </section>

            <section className={styles.choice} aria-labelledby="setup-password-title">
              <h2 id="setup-password-title">{t("auth.setup.passwordTitle")}</h2>
              <p className={styles.help} id="setup-password-help">{t("auth.setup.passwordHelp")}</p>
              <form className={styles.form} onSubmit={submitPassword}>
                <label htmlFor="setup-password">{t("auth.setup.password")}</label>
                <input
                  className={styles.keyboardSafeControl}
                  id="setup-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={busy}
                  aria-describedby="setup-password-help"
                  required
                />
                <label htmlFor="setup-password-confirm">{t("auth.setup.confirmPassword")}</label>
                <input
                  className={styles.keyboardSafeControl}
                  id="setup-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  value={confirmedPassword}
                  onChange={(event) => setConfirmedPassword(event.target.value)}
                  disabled={busy}
                  required
                />
                <button className={styles.secondaryButton} type="submit" disabled={busy}>
                  {busy ? t("auth.common.working") : t("auth.setup.continuePassword")}
                </button>
              </form>
            </section>
          </div>
        ) : registrationState === "password_set" ? (
          material === null ? (
            <section className={styles.choice} aria-labelledby="setup-totp-title">
              <h2 id="setup-totp-title">{t("auth.setup.totpTitle")}</h2>
              <p className={styles.help}>{t("auth.setup.totpHelp")}</p>
              <button className={styles.primaryButton} type="button" onClick={retryTotp} disabled={busy}>
                {busy ? t("auth.common.working") : t("auth.setup.retryTotp")}
              </button>
            </section>
          ) : (
            <section className={styles.totpSetup} aria-labelledby="setup-totp-title">
              <h2 id="setup-totp-title">{t("auth.setup.totpTitle")}</h2>
              <p className={styles.help} id="setup-totp-help">{t("auth.setup.totpHelp")}</p>
              <img className={styles.qrCode} src={material.qrDataUrl} alt={t("auth.setup.totpTitle")} />
              <div className={styles.backupCodes}>
                <h3>{t("auth.setup.backupCodesTitle")}</h3>
                <p className={styles.help}>{t("auth.setup.backupCodesHelp")}</p>
                <ul>
                  {material.backupCodes.map((code, index) => <li key={index}><code>{code}</code></li>)}
                </ul>
              </div>
              <form className={styles.form} onSubmit={submitTotp}>
                <label htmlFor="setup-totp-code">{t("auth.setup.totpCode")}</label>
                <input
                  className={styles.keyboardSafeControl}
                  id="setup-totp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  minLength={6}
                  maxLength={6}
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value)}
                  disabled={busy}
                  aria-describedby="setup-totp-help"
                  required
                />
                <button className={styles.primaryButton} type="submit" disabled={busy}>
                  {busy ? t("auth.common.working") : t("auth.setup.verifyTotp")}
                </button>
              </form>
            </section>
          )
        ) : null}

        <div className={styles.messageRegion} data-error={errorKey !== null} aria-live="polite" aria-atomic="true">
          <p className={styles.errorMessage}>{errorKey === null ? "\u00a0" : t(errorKey)}</p>
        </div>
      </div>
    </section>
  );
}
